// Public landing surface — Pencil "Landing Page v2". Mounted at "/".
//
// Eight sections, top to bottom:
//   01 Header (logo + "See it in action" + primary CTA)
//   02 Hero (eyebrow + serif headline + sub + buttons + proof card)
//   03 Pain cards (3 across)
//   04 Questions you can't answer today (6 pills swap a demo video)
//   05 Founder note
//   06 How it works (3 steps)
//   07 Founding member CTA (left bullets + right form)
//   08 Footer
//
// The header CTA, the hero "Apply" button, and the hero "See it in
// action" secondary all scroll to section 07 — the founding-member
// application form on the right column.

import { useMutation } from "@tanstack/react-query";
import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";

import { ApiError, api } from "@/api/client";
import { env } from "@/lib/env";


// ── Founding member form types & API ────────────────────────────────────────

interface FoundingMemberPayload {
  first_name: string;
  last_name: string;
  work_email: string;
  brokerage_name: string;
  account_count_band: string;
  pain_text: string;
}

interface FoundingMemberResponse {
  id: string;
}

function submitFoundingMember(
  body: FoundingMemberPayload,
): Promise<FoundingMemberResponse> {
  return api.post<FoundingMemberResponse>(
    "/api/v1/leads/founding-member",
    body,
  );
}


// ── Question / video catalog ───────────────────────────────────────────────

interface QuestionDef {
  id: string;
  label: string;
  icon: string; // lucide name
  iconClass: string;
  videoSrc: string;
}

const QUESTIONS: QuestionDef[] = [
  {
    id: "deliverables",
    label: "What are my deliverables?",
    icon: "clipboard-list",
    iconClass: "text-teal",
    videoSrc: "/landing/videos/q-deliverables.mp4",
  },
  {
    id: "renewals",
    label: "Which renewals are at risk?",
    icon: "triangle-alert",
    iconClass: "text-ember",
    videoSrc: "/landing/videos/q-renewals-at-risk.mp4",
  },
  {
    id: "owner",
    label: "Who owns the next step?",
    icon: "user-check",
    iconClass: "text-teal",
    videoSrc: "/landing/videos/q-owner.mp4",
  },
  {
    id: "changed",
    label: "What has changed since the last client meeting?",
    icon: "history",
    iconClass: "text-teal",
    videoSrc: "/landing/videos/q-changed-since.mp4",
  },
  {
    id: "cold",
    label: "Which Accounts are going cold?",
    icon: "snowflake",
    iconClass: "text-teal",
    videoSrc: "/landing/videos/q-cold.mp4",
  },
  {
    id: "blocked",
    label: "What work is blocked?",
    icon: "ban",
    iconClass: "text-ember",
    videoSrc: "/landing/videos/q-blocked.mp4",
  },
];

const ACCOUNT_BANDS: { value: string; label: string }[] = [
  { value: "lt_10", label: "Fewer than 10" },
  { value: "10_25", label: "10 to 25" },
  { value: "25_50", label: "25 to 50" },
  { value: "50_100", label: "50 to 100" },
  { value: "100_250", label: "100 to 250" },
  { value: "gt_250", label: "More than 250" },
];


// ── Lightweight icon — a tiny <svg> bridge to Lucide names. We don't
// pull in the full lucide-react package just for the landing page; the
// shapes used are simple enough to inline.

function Icon({
  name, className, size = 14,
}: { name: string; className?: string; size?: number }) {
  // Common shared props.
  const stroke = "currentColor";
  const w = size;
  const h = size;
  const sw = 1.6;
  switch (name) {
    case "arrow-right":
      return (
        <svg width={w} height={h} viewBox="0 0 24 24" fill="none"
             stroke={stroke} strokeWidth={sw} strokeLinecap="round"
             strokeLinejoin="round" className={className} aria-hidden>
          <path d="M5 12h14M13 5l7 7-7 7" />
        </svg>
      );
    case "circle-play":
      return (
        <svg width={w} height={h} viewBox="0 0 24 24" fill="none"
             stroke={stroke} strokeWidth={sw} strokeLinecap="round"
             strokeLinejoin="round" className={className} aria-hidden>
          <circle cx="12" cy="12" r="10" />
          <path d="M10 8l6 4-6 4z" fill={stroke} stroke="none" />
        </svg>
      );
    case "sparkles":
      return (
        <svg width={w} height={h} viewBox="0 0 24 24" fill="none"
             stroke={stroke} strokeWidth={sw} strokeLinecap="round"
             strokeLinejoin="round" className={className} aria-hidden>
          <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6z" />
          <path d="M19 14l.7 1.9L21.5 17l-1.8.5L19 19l-.5-1.5L17 17l1.8-1zM5 16l.6 1.6L7 18l-1.4.5L5 20l-.4-1.5L3 18l1.6-.6z" />
        </svg>
      );
    case "shield-check":
      return (
        <svg width={w} height={h} viewBox="0 0 24 24" fill="none"
             stroke={stroke} strokeWidth={sw} strokeLinecap="round"
             strokeLinejoin="round" className={className} aria-hidden>
          <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      );
    case "check":
      return (
        <svg width={w} height={h} viewBox="0 0 24 24" fill="none"
             stroke={stroke} strokeWidth={sw + 0.4} strokeLinecap="round"
             strokeLinejoin="round" className={className} aria-hidden>
          <path d="M5 12l5 5L20 7" />
        </svg>
      );
    case "lock":
      return (
        <svg width={w} height={h} viewBox="0 0 24 24" fill="none"
             stroke={stroke} strokeWidth={sw} strokeLinecap="round"
             strokeLinejoin="round" className={className} aria-hidden>
          <rect x="4" y="11" width="16" height="10" rx="2" />
          <path d="M8 11V8a4 4 0 018 0v3" />
        </svg>
      );
    case "clipboard-list":
      return (
        <svg width={w} height={h} viewBox="0 0 24 24" fill="none"
             stroke={stroke} strokeWidth={sw} strokeLinecap="round"
             strokeLinejoin="round" className={className} aria-hidden>
          <rect x="6" y="4" width="12" height="17" rx="2" />
          <rect x="9" y="2" width="6" height="4" rx="1" />
          <path d="M9 10h6M9 14h6M9 18h4" />
        </svg>
      );
    case "triangle-alert":
      return (
        <svg width={w} height={h} viewBox="0 0 24 24" fill="none"
             stroke={stroke} strokeWidth={sw} strokeLinecap="round"
             strokeLinejoin="round" className={className} aria-hidden>
          <path d="M12 3l10 18H2z" />
          <path d="M12 10v5M12 18v.5" />
        </svg>
      );
    case "user-check":
      return (
        <svg width={w} height={h} viewBox="0 0 24 24" fill="none"
             stroke={stroke} strokeWidth={sw} strokeLinecap="round"
             strokeLinejoin="round" className={className} aria-hidden>
          <circle cx="9" cy="8" r="4" />
          <path d="M2 21c.5-3.5 3.5-6 7-6s6.5 2.5 7 6" />
          <path d="M16 11l2 2 4-4" />
        </svg>
      );
    case "history":
      return (
        <svg width={w} height={h} viewBox="0 0 24 24" fill="none"
             stroke={stroke} strokeWidth={sw} strokeLinecap="round"
             strokeLinejoin="round" className={className} aria-hidden>
          <path d="M3 12a9 9 0 1 0 3-6.7" />
          <path d="M3 4v5h5" />
          <path d="M12 8v5l3 2" />
        </svg>
      );
    case "snowflake":
      return (
        <svg width={w} height={h} viewBox="0 0 24 24" fill="none"
             stroke={stroke} strokeWidth={sw} strokeLinecap="round"
             strokeLinejoin="round" className={className} aria-hidden>
          <path d="M12 3v18M3 12h18M5 5l14 14M19 5L5 19" />
          <path d="M9 6l3-3 3 3M9 18l3 3 3-3M6 9L3 12l3 3M18 9l3 3-3 3" />
        </svg>
      );
    case "ban":
      return (
        <svg width={w} height={h} viewBox="0 0 24 24" fill="none"
             stroke={stroke} strokeWidth={sw} strokeLinecap="round"
             strokeLinejoin="round" className={className} aria-hidden>
          <circle cx="12" cy="12" r="9" />
          <path d="M5.6 5.6l12.8 12.8" />
        </svg>
      );
    case "file-spreadsheet":
      return (
        <svg width={w} height={h} viewBox="0 0 24 24" fill="none"
             stroke={stroke} strokeWidth={sw} strokeLinecap="round"
             strokeLinejoin="round" className={className} aria-hidden>
          <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" />
          <path d="M14 3v5h5" />
          <path d="M8 13h9M8 17h9M11 13v4" />
        </svg>
      );
    case "repeat-2":
      return (
        <svg width={w} height={h} viewBox="0 0 24 24" fill="none"
             stroke={stroke} strokeWidth={sw} strokeLinecap="round"
             strokeLinejoin="round" className={className} aria-hidden>
          <path d="M2 9l3-3 3 3" />
          <path d="M5 6h12a3 3 0 013 3v3" />
          <path d="M22 15l-3 3-3-3" />
          <path d="M19 18H7a3 3 0 01-3-3v-3" />
        </svg>
      );
    case "message-circle":
      return (
        <svg width={w} height={h} viewBox="0 0 24 24" fill="none"
             stroke={stroke} strokeWidth={sw} strokeLinecap="round"
             strokeLinejoin="round" className={className} aria-hidden>
          <path d="M21 12a8.5 8.5 0 11-3.4-6.8L21 4l-1.2 3.4A8.5 8.5 0 0121 12z" />
        </svg>
      );
    case "list-checks":
      return (
        <svg width={w} height={h} viewBox="0 0 24 24" fill="none"
             stroke={stroke} strokeWidth={sw} strokeLinecap="round"
             strokeLinejoin="round" className={className} aria-hidden>
          <path d="M3 5l2 2 4-4" />
          <path d="M3 12l2 2 4-4" />
          <path d="M3 19l2 2 4-4" />
          <path d="M13 6h8M13 13h8M13 20h8" />
        </svg>
      );
    case "circle-check":
      return (
        <svg width={w} height={h} viewBox="0 0 24 24" fill="none"
             stroke={stroke} strokeWidth={sw} strokeLinecap="round"
             strokeLinejoin="round" className={className} aria-hidden>
          <circle cx="12" cy="12" r="9" />
          <path d="M8 12l3 3 5-5" />
        </svg>
      );
    case "gauge":
      return (
        <svg width={w} height={h} viewBox="0 0 24 24" fill="none"
             stroke={stroke} strokeWidth={sw} strokeLinecap="round"
             strokeLinejoin="round" className={className} aria-hidden>
          <path d="M12 14l4-4" />
          <path d="M3.5 17a9 9 0 1117 0" />
        </svg>
      );
    case "chevron-down":
      return (
        <svg width={w} height={h} viewBox="0 0 24 24" fill="none"
             stroke={stroke} strokeWidth={sw} strokeLinecap="round"
             strokeLinejoin="round" className={className} aria-hidden>
          <path d="M6 9l6 6 6-6" />
        </svg>
      );
    default:
      return null;
  }
}


// ── Section: 01 Header ──────────────────────────────────────────────────────

function Header({ onCtaClick }: { onCtaClick: () => void }) {
  return (
    <header
      className="flex items-center justify-between border-b border-border-subtle bg-canvas px-6 py-5 md:px-20"
      data-testid="landing-header"
    >
      <Link to="/" className="flex items-center" aria-label="BeneCRM home">
        <img
          src="/brand/benecrm-logo.png"
          alt="BeneCRM"
          className="h-7 w-auto md:h-8"
        />
      </Link>
      <nav className="flex items-center gap-6">
        <button
          type="button"
          onClick={onCtaClick}
          className="hidden text-sm font-medium text-text-secondary transition-fast ease-out hover:text-text-primary md:inline"
          data-testid="landing-nav-link"
        >
          See it in action
        </button>
        <a
          href={`${env.appOrigin}/login`}
          className="text-sm font-medium text-text-secondary transition-fast ease-out hover:text-text-primary"
          data-testid="landing-login-link"
        >
          Log in
        </a>
        <button
          type="button"
          onClick={onCtaClick}
          className="rounded-md bg-teal px-4 py-2 text-sm font-medium text-text-inverse transition-base ease-out hover:opacity-90"
          data-testid="landing-nav-cta"
        >
          Apply as a founding member
        </button>
      </nav>
    </header>
  );
}


// ── Section: 02 Hero ────────────────────────────────────────────────────────

function HeroProofCard() {
  return (
    <div
      className="rounded-2xl border border-border-subtle bg-raised p-7 shadow-overlay"
      style={{ boxShadow: "0 8px 32px 0 rgba(11,11,15,0.06)" }}
    >
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <p className="font-mono text-[11px] font-medium tracking-[0.12em] text-text-tertiary">
            TODAY'S BROKERAGE SNAPSHOT
          </p>
          <p className="text-[18px] font-semibold text-text-primary">
            Operations dashboard
          </p>
        </div>
        <div className="flex items-center gap-1.5 rounded-full bg-[#E4EFEC] px-2.5 py-1">
          <span className="h-1.5 w-1.5 rounded-full bg-teal" />
          <span className="text-[11px] font-medium text-teal">Live</span>
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-3">
        <div className="flex gap-3">
          <ProofStat label="Open commitments" value="42" tone="default" />
          <ProofStat label="Past due" value="3" tone="warn" />
          <ProofStat label="Closing this week" value="11" tone="default" />
        </div>
        <ul className="overflow-hidden rounded-[10px] border border-border-subtle bg-white">
          <ProofRow
            title="Renewal — Ridgemont Manufacturing"
            meta="Owner: Lila Hayes  ·  Due Fri"
            tone="default"
          />
          <ProofRow
            title="ACA filing — Northwind"
            meta="Owner: Sam Ortiz  ·  4 days ago"
            tone="warn"
          />
          <ProofRow
            title="Open enrollment kickoff — Acme"
            meta="Owner: Riya Okafor  ·  Tue"
            tone="default"
            last
          />
        </ul>
      </div>

      <div className="mt-2 flex items-center gap-2 pt-2">
        <Icon name="sparkles" size={14} className="text-teal" />
        <p className="text-[13px] italic text-text-secondary">
          Know what needs attention before a client asks.
        </p>
      </div>
    </div>
  );
}

function ProofStat({
  label, value, tone,
}: { label: string; value: string; tone: "default" | "warn" }) {
  const bg = tone === "warn" ? "bg-ember-tint" : "bg-canvas";
  const valueClass = tone === "warn" ? "text-ember" : "text-text-primary";
  return (
    <div className={`flex flex-1 flex-col gap-1.5 rounded-[10px] ${bg} px-4 py-3.5`}>
      <span className="font-mono text-[10px] font-medium uppercase tracking-[0.1em] text-text-tertiary">
        {label}
      </span>
      <span className={`text-[22px] font-semibold ${valueClass}`}>{value}</span>
    </div>
  );
}

function ProofRow({
  title, meta, tone, last,
}: {
  title: string;
  meta: string;
  tone: "default" | "warn";
  last?: boolean;
}) {
  return (
    <li
      className={`flex items-center justify-between px-4 py-3.5 ${
        last ? "" : "border-b border-[#F0EAD9]"
      }`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`h-1.5 w-1.5 rounded-full ${
            tone === "warn" ? "bg-ember" : "bg-teal"
          }`}
        />
        <span className="text-[13px] font-medium text-text-primary">
          {title}
        </span>
      </div>
      <span className="text-[12px] text-text-tertiary">{meta}</span>
    </li>
  );
}

function Hero({ onPrimaryClick, onSecondaryClick }: {
  onPrimaryClick: () => void;
  onSecondaryClick: () => void;
}) {
  return (
    <section
      className="grid gap-12 bg-canvas px-6 py-20 md:grid-cols-2 md:gap-16 md:px-20 md:py-24"
      data-testid="landing-hero"
    >
      <div className="flex flex-col gap-7">
        <div className="flex w-fit items-center gap-2 rounded-full border border-border-subtle bg-raised px-3 py-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-teal" />
          <span className="text-xs font-medium text-teal">
            For employee benefits brokerages
          </span>
        </div>
        <h1
          className="font-serif text-[40px] italic leading-[1.05] text-text-primary md:text-[56px] lg:text-[64px]"
          style={{ fontFamily: '"Instrument Serif", Georgia, serif' }}
          data-testid="landing-hero-headline"
        >
          Your brokerage runs on promises. BeneCRM makes sure you keep them.
        </h1>
        <p className="text-[17px] leading-[1.55] text-text-secondary">
          BeneCRM helps employee benefits brokerages track every client
          promise, renewal step, compliance item, and owner in one workflow
          system — so work does not disappear into spreadsheets, inboxes, or
          memory.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={onPrimaryClick}
            className="rounded-md bg-teal px-5 py-3 text-sm font-semibold text-text-inverse transition-base ease-out hover:opacity-90"
            data-testid="hero-primary-cta"
          >
            Apply as a founding member
          </button>
          <button
            type="button"
            onClick={onSecondaryClick}
            className="flex items-center gap-2 rounded-md border border-teal bg-raised px-5 py-3 text-sm font-semibold text-teal transition-base ease-out hover:bg-canvas"
            data-testid="hero-secondary-cta"
          >
            <span>See it in action</span>
            <Icon name="arrow-right" size={14} />
          </button>
        </div>
      </div>
      <div className="flex items-center">
        <div className="w-full">
          <HeroProofCard />
        </div>
      </div>
    </section>
  );
}


// ── Section: 03 Pain Cards ──────────────────────────────────────────────────

interface PainCard {
  icon: string;
  title: string;
  body: string;
}

const PAIN_CARDS: PainCard[] = [
  {
    icon: "file-spreadsheet",
    title: "The renewal tracker only two people understand",
    body: "Your benefits tracker is a spreadsheet with 15 tabs, hidden columns, stale notes, and just enough tribal knowledge to make handoffs risky.",
  },
  {
    icon: "repeat-2",
    title: "Recurring work still falls through the cracks",
    body: "Your team has a recurring reminder to “update the renewal list” — but owners, deadlines, blockers, and client follow-ups still drift across email and meetings.",
  },
  {
    icon: "message-circle",
    title: "The client asks the question everyone dreads",
    body: "“What have you done for us lately?” should not require digging through inboxes, Slack, spreadsheets, meeting notes, and memory.",
  },
];

function PainCards() {
  return (
    <section
      className="border-y border-border-subtle bg-raised px-6 py-16 md:px-20"
      data-testid="landing-pain"
    >
      <div className="grid gap-6 md:grid-cols-3">
        {PAIN_CARDS.map((card) => (
          <div
            key={card.title}
            className="flex flex-col gap-4 rounded-2xl border border-border-subtle bg-canvas p-7"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-[10px] border border-border-subtle bg-white">
              <Icon name={card.icon} size={20} className="text-teal" />
            </div>
            <h3 className="text-[18px] font-semibold leading-[1.3] text-text-primary">
              {card.title}
            </h3>
            <p className="text-sm leading-[1.55] text-text-secondary">
              {card.body}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}


// ── Section: 04 Questions you can't answer today ───────────────────────────

function QuestionsDemo() {
  const [activeId, setActiveId] = useState(QUESTIONS[0].id);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const active = QUESTIONS.find((q) => q.id === activeId) ?? QUESTIONS[0];

  // Restart video when source changes.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.load();
    v.play().catch(() => {
      // Autoplay can be blocked; the controls remain available.
    });
  }, [active.videoSrc]);

  return (
    <section
      className="bg-canvas px-6 py-20 md:px-20 md:py-24"
      data-testid="landing-questions"
    >
      <div className="flex flex-col items-center gap-12">
        <div className="flex flex-col items-center gap-4">
          <h2
            className="max-w-[760px] text-center font-serif text-[40px] italic leading-[1.1] text-text-primary md:text-[52px]"
            style={{ fontFamily: '"Instrument Serif", Georgia, serif' }}
          >
            Questions you can’t answer today.
          </h2>
          <p className="max-w-[560px] text-center text-base leading-[1.55] text-text-secondary">
            BeneCRM turns scattered work into a record your brokerage can
            actually query.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2.5" data-testid="question-pills">
          {QUESTIONS.map((q) => {
            const isActive = q.id === activeId;
            return (
              <button
                key={q.id}
                type="button"
                onClick={() => setActiveId(q.id)}
                aria-pressed={isActive}
                data-testid={`question-pill-${q.id}`}
                className={`flex items-center gap-2 rounded-full border px-4 py-2.5 text-[13px] font-medium transition-base ease-out ${
                  isActive
                    ? "border-teal bg-teal text-text-inverse"
                    : "border-border-subtle bg-raised text-text-primary hover:border-teal"
                }`}
              >
                <Icon
                  name={q.icon}
                  size={14}
                  className={isActive ? "text-text-inverse" : q.iconClass}
                />
                <span>{q.label}</span>
              </button>
            );
          })}
        </div>

        <div
          className="w-full overflow-hidden rounded-2xl border border-border-subtle bg-white shadow-overlay"
          style={{ boxShadow: "0 12px 48px 0 rgba(11,11,15,0.06)" }}
        >
          <div className="flex items-center justify-between border-b border-border-subtle bg-raised px-4 py-3">
            <div className="flex items-center gap-2.5">
              <span className="h-2.5 w-2.5 rounded-full bg-border-subtle" />
              <span className="h-2.5 w-2.5 rounded-full bg-border-subtle" />
              <span className="h-2.5 w-2.5 rounded-full bg-border-subtle" />
            </div>
            <span className="font-mono text-[11px] text-text-tertiary">
              app.benecrm.com / commitments
            </span>
            <div className="flex items-center gap-2">
              <Icon name="lock" size={11} className="text-text-tertiary" />
              <span className="font-mono text-[11px] text-text-tertiary">Secure</span>
            </div>
          </div>
          <div className="bg-canvas">
            <video
              ref={videoRef}
              key={active.videoSrc}
              src={active.videoSrc}
              className="block h-auto w-full"
              autoPlay
              loop
              muted
              playsInline
              controls
              data-testid="question-video"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Icon name="circle-play" size={14} className="text-teal" />
          <p
            className="text-[18px] italic text-teal"
            style={{ fontFamily: '"Instrument Serif", Georgia, serif' }}
          >
            See the operating system behind the answers.
          </p>
        </div>
      </div>
    </section>
  );
}


// ── Section: 05 Founder note ────────────────────────────────────────────────

function FounderNote() {
  return (
    <section
      className="bg-canvas px-6 py-20 md:px-32 md:py-24"
      data-testid="landing-founder"
    >
      <div className="mx-auto flex max-w-[760px] flex-col items-center gap-8 text-center">
        <span
          className="text-[96px] leading-none italic text-teal"
          style={{ fontFamily: '"Instrument Serif", Georgia, serif' }}
          aria-hidden
        >
          “
        </span>
        <p
          className="text-[22px] italic leading-[1.4] text-text-primary md:text-[28px]"
          style={{ fontFamily: '"Instrument Serif", Georgia, serif' }}
        >
          I lived the spreadsheet version of this problem. After years in
          benefits at Mercer and Alliant, I wanted one place to see every
          promise made to a client, who owned it, what was blocked, and what
          needed attention before the client asked.
        </p>
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-full"
            style={{ backgroundColor: "#C9DDD8" }}
          >
            <span className="text-[13px] font-semibold text-teal">DK</span>
          </div>
          <div className="flex flex-col items-start">
            <span className="text-sm font-semibold text-text-primary">
              Dike Kalu, CEBS
            </span>
            <span className="text-[13px] text-text-secondary">
              Founder, BeneCRM
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 rounded-full border border-border-subtle bg-raised px-3 py-1.5">
          <Icon name="shield-check" size={14} className="text-teal" />
          <span className="text-xs font-medium text-teal">
            Built by a former benefits broker
          </span>
        </div>
      </div>
    </section>
  );
}


// ── Section: 06 How it works ────────────────────────────────────────────────

const STEPS: { icon: string; step: string; title: string; body: string }[] = [
  {
    icon: "list-checks",
    step: "STEP 01",
    title: "Set up your Playbooks",
    body: "Turn renewals, RFPs, open enrollment, compliance workflows, and recurring client processes into reusable Playbooks your team can run consistently.",
  },
  {
    icon: "circle-check",
    step: "STEP 02",
    title: "Track every Commitment",
    body: 'Every promise has an Account, owner, due date, status, priority, and audit trail. No more "who owns that?" meetings.',
  },
  {
    icon: "gauge",
    step: "STEP 03",
    title: "See what needs attention",
    body: "Dashboards show overdue work, blocked Commitments, renewal readiness, workload risk, and Accounts going cold before they become fire drills.",
  },
];

function HowItWorks() {
  return (
    <section
      className="bg-canvas px-6 py-20 md:px-32"
      data-testid="landing-how"
    >
      <div className="flex flex-col items-center gap-6">
        <h2 className="text-center text-[28px] font-semibold text-text-primary md:text-[32px]">
          How it works
        </h2>
        <p className="max-w-[560px] text-center text-base leading-[1.5] text-text-secondary">
          Everything your brokerage needs to track promises, renewals, and
          client work in one system.
        </p>
        <div className="mt-6 grid gap-8 md:grid-cols-3 md:gap-12">
          {STEPS.map((s) => (
            <div
              key={s.step}
              className="flex flex-col items-center gap-4 p-2 text-center"
            >
              <div
                className="flex h-12 w-12 items-center justify-center rounded-xl"
                style={{ backgroundColor: "#E4EFEC" }}
              >
                <Icon name={s.icon} size={24} className="text-teal" />
              </div>
              <span className="font-mono text-[11px] font-medium tracking-[0.12em] text-text-tertiary">
                {s.step}
              </span>
              <h3 className="text-[18px] font-semibold text-text-primary">
                {s.title}
              </h3>
              <p className="text-sm leading-[1.55] text-text-secondary">
                {s.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


// ── Section: 07 Founding member CTA ────────────────────────────────────────

function FoundingMemberSection({
  formRef,
}: {
  formRef: React.RefObject<HTMLDivElement>;
}) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [brokerage, setBrokerage] = useState("");
  const [band, setBand] = useState("");
  const [pain, setPain] = useState("");
  const [submittedId, setSubmittedId] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: submitFoundingMember,
    onSuccess: (data) => {
      setSubmittedId(data.id);
    },
  });

  const errorMessage = (() => {
    if (!mutation.error) return null;
    if (mutation.error instanceof ApiError) {
      const detail = (mutation.error.body as { detail?: string } | null)?.detail;
      if (detail === "missing_fields") return "Please fill in every field.";
      if (detail === "invalid_email") return "That doesn't look like a valid email.";
      if (detail === "invalid_account_count") return "Pick an Accounts size from the list.";
      return mutation.error.message;
    }
    return "Something went wrong submitting your application. Try again.";
  })();

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    mutation.mutate({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      work_email: email.trim(),
      brokerage_name: brokerage.trim(),
      account_count_band: band,
      pain_text: pain.trim(),
    });
  }

  const bullets = [
    "No per-seat charge during the first 12 months.",
    "Locked founding-member pricing after launch.",
    "Direct access to the product team for workflow feedback and feature requests.",
    "Best fit for teams managing renewals, open enrollment, compliance work, and client promises across spreadsheets and email.",
  ];

  return (
    <section
      ref={formRef}
      id="join"
      className="bg-teal px-6 py-20 md:px-20 md:py-24"
      data-testid="landing-join"
    >
      <div className="grid gap-12 md:grid-cols-2 md:gap-20">
        <div className="flex flex-col gap-4">
          <span
            className="font-mono text-[11px] font-medium uppercase tracking-[0.14em]"
            style={{ color: "#8FB5AF" }}
          >
            FOUNDING COHORT
          </span>
          <h2
            className="text-[36px] italic leading-[1.1] text-text-inverse md:text-[44px]"
            style={{ fontFamily: '"Instrument Serif", Georgia, serif' }}
          >
            Join as a founding member
          </h2>
          <p
            className="text-base leading-[1.55]"
            style={{ color: "#C8D8D5" }}
          >
            We're accepting a small group of benefits brokerages to shape
            BeneCRM before public launch.
          </p>
          <ul className="mt-3 flex flex-col gap-3.5">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <Icon
                  name="check"
                  size={18}
                  className="mt-0.5 shrink-0"
                  // mint check; not in tokens
                />
                <span className="text-sm leading-[1.5]" style={{ color: "#E6EEED" }}>
                  {b}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl bg-raised p-6 md:p-8">
          {submittedId ? (
            <SubmittedSuccess />
          ) : (
            <form
              onSubmit={onSubmit}
              className="flex flex-col gap-4"
              data-testid="founding-form"
              noValidate
            >
              <div className="grid gap-3 md:grid-cols-2">
                <FormField label="First name">
                  <input
                    type="text"
                    autoComplete="given-name"
                    required
                    data-testid="founding-first-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={inputClass}
                  />
                </FormField>
                <FormField label="Last name">
                  <input
                    type="text"
                    autoComplete="family-name"
                    required
                    data-testid="founding-last-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={inputClass}
                  />
                </FormField>
              </div>
              <FormField label="Work email">
                <input
                  type="email"
                  autoComplete="email"
                  required
                  placeholder="jane@brokerage.com"
                  data-testid="founding-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                />
              </FormField>
              <FormField label="Brokerage name">
                <input
                  type="text"
                  autoComplete="organization"
                  required
                  placeholder="Acme Benefits Group"
                  data-testid="founding-brokerage"
                  value={brokerage}
                  onChange={(e) => setBrokerage(e.target.value)}
                  className={inputClass}
                />
              </FormField>
              <FormField label="How many employer Accounts do you manage?">
                <div className="relative">
                  <select
                    required
                    data-testid="founding-band"
                    value={band}
                    onChange={(e) => setBand(e.target.value)}
                    className={`${inputClass} appearance-none pr-10`}
                  >
                    <option value="" disabled>
                      Select…
                    </option>
                    {ACCOUNT_BANDS.map((b) => (
                      <option key={b.value} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary">
                    <Icon name="chevron-down" size={16} />
                  </span>
                </div>
              </FormField>
              <FormField label="What is the hardest part of managing benefits work today?">
                <textarea
                  rows={4}
                  placeholder="Tell us what keeps you up at night…"
                  data-testid="founding-pain"
                  value={pain}
                  onChange={(e) => setPain(e.target.value)}
                  className={`${inputClass} min-h-[96px] resize-y`}
                />
              </FormField>

              {errorMessage && (
                <div
                  role="alert"
                  data-testid="founding-error"
                  className="rounded-md border border-ember/20 bg-ember-tint px-3 py-2 text-sm text-ember"
                >
                  {errorMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={mutation.isPending}
                data-testid="founding-submit"
                className="mt-1 rounded-md bg-teal px-5 py-3 text-sm font-semibold text-text-inverse transition-base ease-out hover:opacity-90 disabled:opacity-50"
              >
                {mutation.isPending ? "Submitting…" : "Let's talk"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

const inputClass =
  "w-full rounded-md border border-border-subtle bg-white px-3.5 py-3 text-sm text-text-primary outline-none transition-fast ease-out placeholder:text-text-tertiary focus:border-teal focus:shadow-focus";

function FormField({
  label, children,
}: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-text-secondary">{label}</span>
      {children}
    </label>
  );
}

function SubmittedSuccess() {
  return (
    <div
      className="flex flex-col items-center gap-3 py-8 text-center"
      data-testid="founding-success"
    >
      <div
        className="flex h-12 w-12 items-center justify-center rounded-full"
        style={{ backgroundColor: "#E4EFEC" }}
      >
        <Icon name="circle-check" size={24} className="text-teal" />
      </div>
      <h3 className="text-[18px] font-semibold text-text-primary">
        Thanks — your application is in.
      </h3>
      <p className="max-w-[360px] text-sm leading-[1.5] text-text-secondary">
        We read every founding-member application personally. Expect a reply
        within a couple of business days.
      </p>
    </div>
  );
}


// ── Section: 08 Footer ─────────────────────────────────────────────────────

function Footer() {
  return (
    <footer
      className="flex flex-col gap-6 border-t border-border-subtle bg-canvas px-6 py-8 md:flex-row md:items-center md:justify-between md:px-32"
      data-testid="landing-footer"
    >
      <p
        className="max-w-[480px] text-[13px] leading-[1.5] text-text-secondary"
      >
        The workflow platform built for benefits brokers who care about
        keeping promises.
      </p>
      <div className="flex flex-col items-start gap-3 md:items-end">
        <div className="flex gap-6 text-[13px] text-text-secondary">
          <Link to="/privacy" className="hover:text-text-primary">Privacy</Link>
          <Link to="/terms" className="hover:text-text-primary">Terms</Link>
          <a href="mailto:hello@benecrm.com" className="hover:text-text-primary">
            Contact
          </a>
        </div>
        <p className="text-xs text-text-tertiary">
          © 2026 BeneCRM. All rights reserved.
        </p>
      </div>
    </footer>
  );
}


// ── Page ───────────────────────────────────────────────────────────────────

export function LandingPage() {
  const formRef = useRef<HTMLDivElement>(null);

  function scrollToForm() {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div
      className="absolute inset-0 overflow-y-auto bg-canvas"
      data-testid="landing-page"
    >
      <Header onCtaClick={scrollToForm} />
      <Hero
        onPrimaryClick={scrollToForm}
        onSecondaryClick={scrollToForm}
      />
      <PainCards />
      <QuestionsDemo />
      <FounderNote />
      <HowItWorks />
      <FoundingMemberSection formRef={formRef} />
      <Footer />
    </div>
  );
}
